<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { site } from '@/i18n'
import { pages } from '@/i18n'
import KpiCounter from '@/components/KpiCounter.vue'
import Icon from '@/components/Icon.vue'
import PayMark from '@/components/PayMark.vue'

const router = useRouter()
const activeJourney = ref(0)

// 能力全景与安全四层图标映射
const capIcons: Record<string, string> = {
  payment: 'Coin', routing: 'Route', matching: 'Handshake', c2c: 'ShieldChat',
  wallet: 'Wallet', risk: 'Gauge', notify: 'Bell', tenant: 'Layers'
}
const layerIcons = ['Lock', 'Gauge', 'Fingerprint', 'Server']

function go(path: string) {
  router.push(path)
}

function setJourney(index: number) {
  activeJourney.value = index
}

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible')
          observer.unobserve(entry.target)
        }
      })
    },
    { threshold: 0.12 }
  )

  document.querySelectorAll('[data-animate]').forEach((el) => {
    observer.observe(el)
  })
})
</script>

<template>
  <main class="home">
    <!-- S1 Hero -->
    <section id="hero" class="hero">
      <div class="hero-glow"/>
      <div class="container hero-grid">
        <div class="hero-content" :class="{ visible: true }">
          <div class="hero-tag">
            <span class="tag-pulse"/>
            <span>{{ site.home.heroTag }}</span>
          </div>
          <h1 class="hero-title">
            <span class="text-gradient">{{ site.hero.title }}</span>
          </h1>
          <p class="hero-subtitle">{{ site.hero.subtitle }}</p>
          <p class="hero-desc">{{ site.hero.description }}</p>
          <div class="hero-actions">
            <button class="btn btn-primary hero-cta" @click="go(site.hero.primaryLink)">
              {{ site.hero.primaryCta }}
              <Icon name="ArrowRight"/>
            </button>
            <button class="btn btn-secondary" @click="go(site.hero.secondaryLink)">
              {{ site.hero.secondaryCta }}
            </button>
          </div>
        </div>

        <div class="hero-visual" :class="{ visible: true }">
          <div class="hierarchy-card">
            <div class="hierarchy-title">{{ site.home.ecoTitle }}</div>
            <div class="hierarchy-tree">
              <div class="tree-node root">
                <span class="node-dot"/>
                <span>{{ pages.home.strings.nodePlatform }}</span>
              </div>
              <div class="tree-branch">
                <div class="tree-line"/>
              </div>
              <div class="tree-row">
                <div class="tree-node">
                  <span class="node-dot"/>
                  <span>{{ pages.home.strings.nodeOperator }}</span>
                </div>
                <div class="tree-node user">
                  <span class="node-dot"/>
                  <span>{{ pages.home.strings.nodeEndUser }}</span>
                </div>
              </div>
              <div class="tree-branch inner">
                <div class="tree-line short"/>
              </div>
              <div class="tree-row">
                <div class="tree-node">
                  <span class="node-dot"/>
                  <span>{{ pages.home.strings.nodeChannel }}</span>
                </div>
                <div class="tree-node">
                  <span class="node-dot"/>
                  <span>{{ pages.home.strings.nodeMerchant }}</span>
                </div>
              </div>
              <div class="tree-row agents">
                <div class="tree-node agent">
                  <span class="node-dot"/>
                  <span>{{ pages.home.strings.nodeChannelAgent }}</span>
                </div>
                <div class="tree-node agent">
                  <span class="node-dot"/>
                  <span>{{ pages.home.strings.nodeMerchantAgent }}</span>
                </div>
              </div>
            </div>
            <div class="hierarchy-footer">
              <span class="mono">7</span>{{ site.home.ecoFooter }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- S2 KPI -->
    <section id="kpi" class="kpi-band">
      <div class="container">
        <div class="kpi-grid" data-animate id="kpi-section">
          <KpiCounter
            v-for="kpi in site.kpis"
            :key="kpi.label"
            :value="kpi.value"
            :label="kpi.label"
          />
        </div>
      </div>
    </section>

    <!-- S3 Features -->
    <section id="features" class="section features-section">
      <div class="container">
        <div class="section-header" data-animate>
          <h2 class="section-title">{{ site.home.featuresTitle }}</h2>
          <p class="section-subtitle">{{ site.home.featuresSub }}</p>
        </div>
        <div class="features-grid">
          <div
            v-for="(feature, idx) in site.features"
            :key="feature.title"
            class="card feature-card"
            data-animate
            :style="{ transitionDelay: `${idx * 80}ms` }"
          >
            <div class="feature-icon">
              <Icon :name="feature.icon"/>
            </div>
            <h3 class="feature-title">{{ feature.title }}</h3>
            <p class="feature-desc">{{ feature.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- S3.5 Payment Methods -->
    <section id="payments" class="section payments-section">
      <div class="container">
        <div class="section-header" data-animate>
          <h2 class="section-title">{{ site.payments.title }}</h2>
          <p class="section-subtitle">{{ site.payments.subtitle }}</p>
        </div>
        <div class="pay-grid">
          <div
            v-for="(m, idx) in site.payments.methods"
            :key="m.name"
            class="card pay-card"
            data-animate
            :style="{
              '--pm-shine-delay': `${idx * 0.55}s`,
              '--pay-wave-delay': `${idx * 0.28}s`,
              '--pay-breathe-delay': `${idx * 0.35}s`,
              transitionDelay: `${idx * 60}ms`
            }"
          >
            <div class="pay-head">
              <PayMark :tone="m.tone"/>
              <span class="pay-name">{{ m.name }}</span>
            </div>
            <div class="pay-forms">
              <span
                v-for="(f, fidx) in m.forms"
                :key="f"
                class="tag"
                :style="{ '--tag-delay': `${(idx * 0.28 + fidx * 0.12)}s` }"
              >{{ f }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- S3.6 Capability Panorama -->
    <section id="capabilities" class="section cap-section">
      <div class="container">
        <div class="section-header" data-animate>
          <h2 class="section-title">{{ site.home.capTitle }}</h2>
          <p class="section-subtitle">{{ pages.product.subtitle }}</p>
        </div>
        <div class="cap-grid">
          <div
            v-for="(g, idx) in pages.product.groups"
            :key="g.id"
            class="card cap-card"
            data-animate
            :style="{ transitionDelay: `${idx * 60}ms` }"
            @click="go(`/product#${g.id}`)"
          >
            <div class="cap-top">
              <div class="cap-icon"><Icon :name="capIcons[g.id] || 'Layers'"/></div>
              <span class="cap-count mono">{{ g.points.length }} {{ site.home.capUnit }}</span>
            </div>
            <h3 class="cap-title">{{ g.title }}</h3>
            <p class="cap-summary">{{ g.summary }}</p>
            <ul class="cap-points">
              <li v-for="pt in g.points.slice(0, 2)" :key="pt">
                <Icon name="Check"/><span>{{ pt }}</span>
              </li>
            </ul>
            <span class="cap-link">{{ site.home.capLink }} <Icon name="ArrowRight"/></span>
          </div>
        </div>
      </div>
    </section>

    <!-- S4 Journey -->
    <section id="journey" class="section journey-section">
      <div class="container">
        <div class="section-header" data-animate>
          <h2 class="section-title">{{ site.journey.title }}</h2>
          <p class="section-subtitle">{{ site.journey.subtitle }}</p>
        </div>

        <div class="journey-flow" data-animate>
          <div class="journey-track"/>
          <div
            v-for="(step, idx) in site.journey.steps"
            :key="step.id"
            class="journey-node"
            :class="{ active: activeJourney >= idx }"
            @mouseenter="setJourney(idx)"
            @click="setJourney(idx)"
          >
            <div class="journey-dot">{{ step.id }}</div>
            <div class="journey-label">{{ step.title }}</div>
          </div>
        </div>

        <div class="journey-detail card" data-animate>
          <div class="detail-number mono">0{{ site.journey.steps[activeJourney].id }}</div>
          <h3>{{ site.journey.steps[activeJourney].title }}</h3>
          <p>{{ site.journey.steps[activeJourney].desc }}</p>
        </div>
      </div>
    </section>

    <!-- S5 Matching Preview -->
    <section id="matching-preview" class="section preview-section">
      <div class="container preview-grid">
        <div class="preview-content" data-animate>
          <h2 class="section-title">{{ site.matchingPreview.title }}</h2>
          <p class="section-subtitle">{{ site.matchingPreview.subtitle }}</p>
          <div class="mini-steps">
            <div
              v-for="(step, idx) in site.matchingPreview.steps"
              :key="step.title"
              class="mini-step"
            >
              <div class="mini-dot">{{ idx + 1 }}</div>
              <div>
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
              </div>
            </div>
          </div>
          <div class="preview-stats">
            <div v-for="s in site.matchingPreview.stats" :key="s.label" class="pstat">
              <span class="pstat-value mono">{{ s.value }}</span>
              <span class="pstat-label">{{ s.label }}</span>
            </div>
          </div>
          <button class="btn btn-primary" @click="go(site.matchingPreview.link)">
            {{ site.matchingPreview.cta }}
            <Icon name="ArrowRight"/>
          </button>
        </div>

        <div class="preview-visual" data-animate>
          <div class="market-card">
            <div class="market-header">
              <div class="market-avatar">CH</div>
              <div>
                <div class="market-name">{{ pages.home.strings.marketName }}</div>
                <div class="market-meta">{{ pages.home.strings.succRate }} 98.7% · 24h {{ pages.home.strings.online }}</div>
              </div>
            </div>
            <div class="market-tags">
              <span class="tag alipay">{{ pages.home.strings.tagAlipay }}</span>
              <span class="tag wechat">{{ pages.home.strings.tagWechat }}</span>
              <span class="tag usdt">USDT</span>
            </div>
            <div class="market-row">
              <span class="market-label">{{ pages.home.strings.feeRate }}</span>
              <span class="market-value">2.8%</span>
            </div>
            <div class="market-row">
              <span class="market-label">{{ pages.home.strings.singleLimit }}</span>
              <span class="market-value mono">¥100 - ¥50,000</span>
            </div>
            <div class="market-row">
              <span class="market-label">{{ pages.home.strings.deposit }}</span>
              <span class="market-value success">{{ pages.home.strings.noDeposit }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- S6 C2C Preview -->
    <section id="c2c-preview" class="section preview-section alt">
      <div class="container preview-grid reverse">
        <div class="preview-visual" data-animate>
          <div class="chat-mock">
            <div class="chat-header">
              <span class="chat-dot online"/>
              <span>{{ pages.home.strings.escrowAssistant }}</span>
            </div>
            <div class="chat-body">
              <div class="chat-bubble received">
                {{ pages.home.strings.buyerPaid }}
              </div>
              <div class="chat-bubble guarantee">
                <div class="guarantee-icon"><Icon name="Shield"/></div>
                <div class="guarantee-content">
                  <div class="guarantee-title">{{ pages.home.strings.guaranteeTrade }}</div>
                  <div class="guarantee-item">{{ pages.home.strings.goodsLabel }}：USDT
                  </div>
                  <div class="guarantee-item">{{ pages.home.strings.amountLabel }}：¥5,000</div>
                  <div class="guarantee-actions">
                    <button class="guarantee-btn confirm">{{ pages.home.strings.releaseBtn }}</button>
                    <button class="guarantee-btn dispute">{{ pages.home.strings.appealBtn }}</button>
                  </div>
                </div>
              </div>
              <div class="chat-bubble transfer self">
                <div class="transfer-icon"><Icon name="Coin"/></div>
                <div class="transfer-content">
                  <div class="transfer-title">{{ pages.home.strings.transferTitle }}</div>
                  <div class="transfer-amount">+ ¥2,000.00</div>
                  <div class="transfer-status">{{ pages.home.strings.arrived }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="preview-content" data-animate>
          <h2 class="section-title">{{ site.c2cPreview.title }}</h2>
          <p class="section-subtitle">{{ site.c2cPreview.subtitle }}</p>
          <div class="mini-steps">
            <div
              v-for="(step, idx) in site.c2cPreview.steps"
              :key="step.title"
              class="mini-step"
            >
              <div class="mini-dot">{{ idx + 1 }}</div>
              <div>
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
              </div>
            </div>
          </div>
          <div class="preview-stats">
            <div v-for="s in site.c2cPreview.stats" :key="s.label" class="pstat">
              <span class="pstat-value mono">{{ s.value }}</span>
              <span class="pstat-label">{{ s.label }}</span>
            </div>
          </div>
          <button class="btn btn-primary" @click="go(site.c2cPreview.link)">
            {{ site.c2cPreview.cta }}
            <Icon name="ArrowRight"/>
          </button>
        </div>
      </div>
    </section>

    <!-- S7 Security Preview -->
    <section id="security-preview" class="section security-section">
      <div class="container security-grid">
        <div class="security-content" data-animate>
          <h2 class="section-title">{{ site.securityPreview.title }}</h2>
          <p class="section-subtitle">{{ site.securityPreview.subtitle }}</p>
          <div class="layer-grid">
            <div v-for="(layer, i) in pages.security.layers" :key="layer.title" class="layer-card">
              <div class="layer-icon"><Icon :name="layerIcons[i]"/></div>
              <div class="layer-body">
                <h4>{{ layer.title }}</h4>
                <p v-for="pt in layer.cards.slice(0, 2)" :key="pt">{{ pt }}</p>
              </div>
            </div>
          </div>
          <button class="btn btn-primary" @click="go(site.securityPreview.link)">
            {{ site.securityPreview.cta }}
            <Icon name="ArrowRight"/>
          </button>
        </div>
        <div class="security-shield" data-animate>
          <div class="shield-ring"/>
          <div class="shield-core">
            <svg viewBox="0 0 120 140" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M60 4L8 32v36c0 42 22 54 52 68 30-14 52-26 52-68V32L60 4z" fill="rgba(var(--u2-primary-rgb), 0.07)" stroke="var(--u2-primary)" stroke-width="3"/>
              <path d="M38 68l16 16 28-32" stroke="var(--u2-primary)" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
    </section>

    <!-- S7.5 Settlement Modes -->
    <section id="settlement" class="section settlement-section">
      <div class="container">
        <div class="section-header" data-animate>
          <h2 class="section-title">{{ site.settlement.title }}</h2>
          <p class="section-subtitle">{{ site.settlement.subtitle }}</p>
        </div>
        <div class="settle-grid">
          <div
            v-for="(m, idx) in site.settlement.modes"
            :key="m.mode"
            class="card settle-card"
            data-animate
            :style="{ transitionDelay: `${idx * 60}ms` }"
          >
            <div class="settle-head">
              <span class="settle-mode mono">{{ m.mode }}</span>
              <span v-if="m.tag" class="settle-tag">{{ m.tag }}</span>
            </div>
            <p class="settle-desc">{{ m.desc }}</p>
            <div class="settle-bar"><span :style="{ width: m.mode === 'T0' ? '100%' : m.mode === 'D0' ? '82%' : m.mode === 'T1' ? '60%' : '46%' }"/></div>
            <span class="settle-note">{{ site.home.settleNote }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- S7.6 Developer -->
    <section id="dev" class="section dev-section">
      <div class="container dev-grid">
        <div class="dev-content" data-animate>
          <h2 class="section-title">{{ site.dev.title }}</h2>
          <p class="section-subtitle">{{ site.dev.subtitle }}</p>
          <div class="dev-steps">
            <div v-for="(step, idx) in pages.developers.steps" :key="step.title" class="dev-step">
              <span class="dev-num mono">{{ idx + 1 }}</span>
              <div>
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
              </div>
            </div>
          </div>
          <button class="btn btn-primary" @click="go('/developers')">
            {{ site.home.devDocLink }}
            <Icon name="ArrowRight"/>
          </button>
        </div>
        <div class="dev-visual" data-animate>
          <div class="code-card">
            <div class="code-bar">
              <span class="code-dot"/><span class="code-dot"/><span class="code-dot"/>
              <span class="code-badge mono">{{ site.dev.signBadge }}</span>
            </div>
            <pre class="code-body mono"><span v-for="(line, i) in site.dev.code" :key="i" class="code-line">{{ line }}
</span></pre>
          </div>
        </div>
      </div>
    </section>

    <!-- S8 Role Entries -->
    <section id="roles" class="section roles-section">
      <div class="container">
        <div class="section-header" data-animate>
          <h2 class="section-title">{{ site.home.rolesTitle }}</h2>
          <p class="section-subtitle">{{ site.home.rolesSub }}</p>
        </div>
        <div class="roles-grid">
          <div
            v-for="(role, idx) in site.roleEntries"
            :key="role.role"
            class="card role-card"
            data-animate
            :style="{ transitionDelay: `${idx * 60}ms` }"
            @click="go('/roles')"
          >
            <div class="role-icon">
              <Icon :name="role.icon"/>
            </div>
            <h3 class="role-name">{{ role.role }}</h3>
            <p class="role-desc">{{ role.desc }}</p>
            <span class="role-link">{{ site.home.roleLink }} <Icon name="ArrowRight"/></span>
          </div>
        </div>
      </div>
    </section>

    <!-- S9 Onboarding -->
    <section id="onboarding" class="section onboarding-section">
      <div class="container">
        <div class="section-header center" data-animate>
          <h2 class="section-title">{{ site.onboarding.title }}</h2>
          <p class="section-subtitle">{{ site.home.onboardingSub }}</p>
        </div>
        <div class="onboarding-timeline" data-animate>
          <div class="timeline-line"/>
          <div
            v-for="(step, idx) in site.onboarding.steps"
            :key="step.title"
            class="timeline-step"
          >
            <div class="timeline-dot">{{ idx + 1 }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
          </div>
        </div>
        <div class="onboarding-cta" data-animate>
          <button class="btn btn-primary btn-lg" @click="go('/login')">
            {{ site.home.registerCta }}
            <Icon name="ArrowRight"/>
          </button>
        </div>
      </div>
    </section>

    <!-- S10 Final CTA -->
    <section id="final-cta" class="final-cta-section">
      <div class="container">
        <div class="final-cta-card" data-animate>
          <h2>{{ site.finalCta.title }}</h2>
          <p>{{ site.finalCta.subtitle }}</p>
          <div class="final-actions">
            <button class="btn btn-primary btn-lg" @click="go(site.finalCta.primary.link)">
              {{ site.finalCta.primary.label }}
              <Icon name="ArrowRight"/>
            </button>
            <button class="btn btn-secondary btn-lg" @click="go(site.finalCta.secondary.link)">
              <Icon name="Download"/>
              {{ site.finalCta.secondary.label }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped>
.home {
  padding-top: var(--header-height);
}

/* Hero */
.hero {
  position: relative;
  min-height: calc(100vh - var(--header-height));
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: var(--space-12) 0;
}

.hero-glow {
  position: absolute;
  top: -20%;
  right: -10%;
  width: 70vw;
  height: 70vw;
  background: radial-gradient(circle, rgba(var(--u2-primary-rgb), 0.05) 0%, transparent 60%);
  pointer-events: none;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: var(--space-12);
  align-items: center;
  position: relative;
  z-index: 1;
}

.hero-content {
  opacity: 0;
  transform: translateY(24px);
  animation: fadeInUp 0.9s var(--ease-out-expo) forwards;
}

.hero-content.visible {
  opacity: 1;
}

.hero-tag {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-full);
  border: 1px solid var(--u2-stroke);
  background: var(--u2-fill);
  color: var(--u2-muted);
  font-size: 13px;
  margin-bottom: var(--space-6);
  box-shadow: var(--shadow-sm);
}

.tag-pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--u2-primary);
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.hero-title {
  font-size: clamp(44px, 6vw, 72px);
  font-weight: 800;
  line-height: 1.05;
  margin: 0 0 var(--space-4);
  letter-spacing: -0.03em;
}

.hero-subtitle {
  font-size: clamp(20px, 2.5vw, 28px);
  color: var(--u2-text);
  font-weight: 700;
  margin: 0 0 var(--space-4);
  max-width: 560px;
}

.hero-desc {
  color: var(--u2-muted);
  font-size: 16px;
  max-width: 520px;
  margin: 0 0 var(--space-8);
}

.hero-actions {
  display: flex;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.hero-cta {
  padding: var(--space-4) var(--space-8);
  font-size: 16px;
}

.hero-visual {
  opacity: 0;
  transform: translateY(24px);
  animation: fadeInUp 0.9s var(--ease-out-expo) 0.15s forwards;
}

.hierarchy-card {
  background: var(--u2-fill);
  border: 1px solid var(--u2-stroke);
  border-radius: var(--radius-xl);
  padding: var(--space-8);
  box-shadow: var(--shadow-lg);
}

.hierarchy-title {
  font-size: 14px;
  color: var(--u2-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: var(--space-6);
}

.hierarchy-tree {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.tree-node {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: var(--u2-hover);
  border: 1px solid var(--u2-stroke);
  border-radius: var(--radius-md);
  font-size: 13px;
  font-weight: 500;
}

.tree-node.root {
  background: rgba(var(--u2-primary-rgb), 0.05);
  border-color: rgba(var(--u2-primary-rgb), 0.25);
  color: var(--u2-emphasis);
}

.tree-node.user {
  background: rgba(8, 145, 178, 0.06);
  border-color: rgba(8, 145, 178, 0.25);
  color: var(--u2-info);
}

.tree-node.agent {
  font-size: 12px;
  opacity: 0.85;
}

.node-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--u2-primary);
}

.tree-node.user .node-dot {
  background: var(--u2-info);
}

.tree-branch {
  display: flex;
  justify-content: center;
}

.tree-line {
  width: 2px;
  height: 20px;
  background: linear-gradient(180deg, var(--u2-stroke), transparent);
}

.tree-line.short {
  height: 14px;
}

.tree-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

.tree-row.agents {
  padding: 0 var(--space-8);
}

.hierarchy-footer {
  margin-top: var(--space-6);
  padding-top: var(--space-5);
  border-top: 1px solid var(--u2-stroke);
  font-size: 13px;
  color: var(--u2-muted);
}

.hierarchy-footer span {
  color: var(--u2-primary);
  font-weight: 700;
  font-size: 18px;
}

/* KPI */
.kpi-band {
  background: var(--u2-secondary);
  border-bottom: 1px solid var(--u2-stroke);
  padding: var(--space-10) 0;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--space-6);
  opacity: 0;
  transform: translateY(24px);
  transition: all 0.7s var(--ease-out-expo);
}

.kpi-grid.visible {
  opacity: 1;
  transform: translateY(0);
}

/* Features */
.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-5);
}

.feature-card {
  padding: var(--space-8);
}

.feature-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  background: rgba(var(--u2-primary-rgb), 0.06);
  color: var(--u2-primary);
  margin-bottom: var(--space-5);
}

.feature-icon svg {
  width: 26px;
  height: 26px;
}

.feature-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 var(--space-2);
}

.feature-desc {
  color: var(--u2-muted);
  font-size: 14px;
  line-height: 1.7;
  margin: 0;
}

/* Payment Methods */
.payments-section {
  background: var(--u2-secondary);
  border-top: 1px solid var(--u2-stroke);
  border-bottom: 1px solid var(--u2-stroke);
}

.pay-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--space-4);
}

.pay-card {
  position: relative;
  overflow: hidden;
  padding: var(--space-6) var(--space-5);
  /* 入场初态 (Observer 入场前) */
  opacity: 0;
  transform: translateY(22px);
  transition:
    transform 0.35s var(--ease-out-expo),
    box-shadow 0.35s var(--ease-out-expo),
    border-color 0.35s ease,
    opacity 0.7s var(--ease-out-expo);
}

/* 卡片入场 (去永久呼吸辉光 + 去红光扫描波 —— 克制企业风) */
.pay-card.visible {
  opacity: 1;
  transform: translateY(0);
}

.pay-card::before {
  content: none;
}

.pay-card:hover {
  transform: translateY(-2px);
  border-color: var(--u2-stroke-strong);
  box-shadow: var(--shadow-md);
}

.pay-head {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
  transition: transform 0.35s var(--ease-out-expo);
}

.pay-card:hover .pay-head {
  transform: translateX(3px);
}

.pay-name {
  font-weight: 700;
  font-size: 15px;
  color: var(--u2-text);
}

.pay-forms {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.pay-forms .tag {
  font-size: 11px;
  padding: 2px var(--space-2);
  transition: color 0.3s ease, background 0.3s ease, border-color 0.3s ease, transform 0.3s ease;
}

.pay-card:hover .tag {
  color: var(--u2-text);
  background: var(--u2-hover);
  border-color: var(--u2-stroke-strong);
}


@keyframes pay-wave {
  0%   { transform: translateX(-130%); }
  45%  { transform: translateX(130%); }
  100% { transform: translateX(130%); }
}

@keyframes pay-breathe {
  0%, 100% { box-shadow: 0 0 0 0 rgba(var(--u2-primary-rgb), 0.00); }
  50%      { box-shadow: 0 0 0 2px rgba(var(--u2-primary-rgb), 0.07); }
}

@keyframes tag-float {
  0%, 100% { transform: translateY(0); opacity: 1; }
  50%      { transform: translateY(-1px); opacity: 0.9; }
}

@media (prefers-reduced-motion: reduce) {
  .pay-card::before,
  .pay-card.visible,
  .pay-card.visible .tag { animation: none !important; }
}

/* Capability Panorama */
.cap-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-5);
}

.cap-card {
  padding: var(--space-6);
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.cap-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-4);
}

.cap-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  background: rgba(var(--u2-primary-rgb), 0.06);
  color: var(--u2-primary);
}

.cap-icon svg {
  width: 24px;
  height: 24px;
}

.cap-count {
  font-size: 12px;
  color: var(--u2-muted);
  background: var(--u2-hover);
  border: 1px solid var(--u2-stroke);
  padding: 2px var(--space-3);
  border-radius: var(--radius-full);
}

.cap-title {
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 var(--space-2);
}

.cap-summary {
  color: var(--u2-muted);
  font-size: 13px;
  line-height: 1.7;
  margin: 0 0 var(--space-4);
}

.cap-points {
  list-style: none;
  margin: 0 0 var(--space-4);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.cap-points li {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  font-size: 12px;
  color: var(--u2-muted);
  line-height: 1.5;
}

.cap-points svg {
  width: 14px;
  height: 14px;
  color: var(--u2-success);
  flex-shrink: 0;
  margin-top: 2px;
}

.cap-link {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: 13px;
  font-weight: 700;
  color: var(--u2-primary);
}

.cap-link svg {
  width: 14px;
  height: 14px;
}

/* Journey */
.journey-section {
  background: linear-gradient(180deg, var(--u2-bg) 0%, var(--u2-secondary) 50%, var(--u2-bg) 100%);
}

.journey-flow {
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: var(--space-2);
  margin-bottom: var(--space-10);
}

.journey-track {
  position: absolute;
  top: 20px;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--u2-stroke);
}

.journey-node {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
  z-index: 1;
}

.journey-dot {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--u2-fill);
  border: 2px solid var(--u2-stroke);
  color: var(--u2-muted);
  font-weight: 700;
  font-size: 14px;
  transition: all 0.3s var(--ease-out-expo);
}

.journey-node.active .journey-dot,
.journey-node:hover .journey-dot {
  background: var(--u2-primary);
  border-color: var(--u2-primary);
  color: #fff;
  box-shadow: var(--shadow-md);
}

.journey-label {
  font-size: 13px;
  color: var(--u2-muted);
  text-align: center;
  max-width: 90px;
  transition: color 0.3s ease;
}

.journey-node.active .journey-label,
.journey-node:hover .journey-label {
  color: var(--u2-text);
}

.journey-detail {
  padding: var(--space-8);
  max-width: 720px;
  margin: 0 auto;
}

.detail-number {
  font-size: 48px;
  font-weight: 700;
  color: rgba(var(--u2-primary-rgb), 0.16);
  line-height: 1;
  margin-bottom: var(--space-3);
}

.journey-detail h3 {
  font-size: 22px;
  margin: 0 0 var(--space-3);
}

.journey-detail p {
  color: var(--u2-muted);
  margin: 0;
}

/* Preview sections */
.preview-section {
  overflow: hidden;
}

.preview-section.alt {
  background: var(--u2-secondary);
  border-top: 1px solid var(--u2-stroke);
  border-bottom: 1px solid var(--u2-stroke);
}

.preview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-12);
  align-items: center;
}

.preview-grid.reverse {
  direction: rtl;
}

.preview-grid.reverse > * {
  direction: ltr;
}

.preview-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.mini-steps {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin: var(--space-6) 0 var(--space-8);
  width: 100%;
}

.mini-step {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
}

.mini-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--u2-primary-rgb), 0.06);
  border: 1px solid rgba(var(--u2-primary-rgb), 0.22);
  color: var(--u2-primary);
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
}

.mini-step h4 {
  margin: 0 0 var(--space-1);
  font-size: 16px;
}

.mini-step p {
  margin: 0;
  color: var(--u2-muted);
  font-size: 14px;
}

.preview-visual {
  display: flex;
  justify-content: center;
}

.preview-stats {
  display: flex;
  gap: var(--space-8);
  margin-bottom: var(--space-8);
  padding: var(--space-4) var(--space-6);
  background: var(--u2-fill);
  border: 1px solid var(--u2-stroke);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.pstat {
  display: flex;
  flex-direction: column;
}

.pstat-value {
  font-size: 22px;
  font-weight: 700;
  color: var(--u2-primary);
  line-height: 1.2;
}

.pstat-label {
  font-size: 12px;
  color: var(--u2-muted);
}

/* Market card */
.market-card {
  width: 100%;
  max-width: 360px;
  background: var(--u2-fill);
  border: 1px solid var(--u2-stroke);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  box-shadow: var(--shadow-lg);
}

.market-header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.market-avatar {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, var(--u2-primary), var(--u2-emphasis));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
}

.market-name {
  font-weight: 700;
}

.market-meta {
  font-size: 12px;
  color: var(--u2-success);
}

.market-tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-5);
}

.market-tags .tag {
  font-size: 11px;
  padding: var(--space-1) var(--space-2);
}

.tag.alipay { background: rgba(2, 132, 199, 0.08); border-color: rgba(2, 132, 199, 0.25); color: #0284C7; }
.tag.wechat { background: rgba(14, 159, 87, 0.08); border-color: rgba(14, 159, 87, 0.25); color: var(--u2-success); }
.tag.usdt { background: rgba(15, 118, 110, 0.08); border-color: rgba(15, 118, 110, 0.25); color: #0F766E; }

.market-row {
  display: flex;
  justify-content: space-between;
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--u2-stroke);
  font-size: 14px;
}

.market-label {
  color: var(--u2-muted);
}

.market-value {
  font-weight: 700;
}

.market-value.success {
  color: var(--u2-success);
}

/* Chat mock */
.chat-mock {
  width: 100%;
  max-width: 360px;
  background: var(--u2-fill);
  border: 1px solid var(--u2-stroke);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

.chat-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--u2-secondary);
  border-bottom: 1px solid var(--u2-stroke);
  font-size: 14px;
  font-weight: 500;
}

.chat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--u2-success);
}

.chat-body {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.chat-bubble {
  max-width: 80%;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-lg);
  font-size: 13px;
  line-height: 1.5;
}

.chat-bubble.received {
  align-self: flex-start;
  background: var(--u2-secondary);
  color: var(--u2-text);
}

.chat-bubble.guarantee {
  align-self: flex-start;
  background: rgba(8, 145, 178, 0.06);
  border: 1px solid rgba(8, 145, 178, 0.22);
  display: flex;
  gap: var(--space-3);
}

.guarantee-icon {
  width: 24px;
  height: 24px;
  color: var(--u2-info);
  flex-shrink: 0;
}

.guarantee-icon svg {
  width: 24px;
  height: 24px;
}

.guarantee-title {
  font-weight: 700;
  color: var(--u2-info);
  margin-bottom: var(--space-1);
}

.guarantee-item {
  color: var(--u2-muted);
  font-size: 12px;
}

.guarantee-actions {
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-3);
}

.guarantee-btn {
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-sm);
  border: none;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.guarantee-btn.confirm {
  background: var(--u2-success);
  color: #fff;
}

.guarantee-btn.dispute {
  background: transparent;
  border: 1px solid var(--u2-stroke);
  color: var(--u2-muted);
}

.chat-bubble.transfer {
  align-self: flex-end;
  background: rgba(var(--u2-primary-rgb), 0.05);
  border: 1px solid rgba(var(--u2-primary-rgb), 0.2);
  display: flex;
  gap: var(--space-3);
}

.transfer-icon {
  width: 24px;
  height: 24px;
  color: var(--u2-primary);
  flex-shrink: 0;
}

.transfer-icon svg {
  width: 24px;
  height: 24px;
}

.transfer-title {
  font-weight: 700;
  color: var(--u2-primary);
}

.transfer-amount {
  font-size: 18px;
  font-weight: 700;
}

.transfer-status {
  color: var(--u2-success);
  font-size: 12px;
}

/* Security */
.security-section {
  background: radial-gradient(circle at 70% 50%, rgba(var(--u2-primary-rgb), 0.04) 0%, transparent 50%);
}

.security-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-12);
  align-items: center;
}

.layer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
  margin: var(--space-6) 0 var(--space-8);
}

.layer-card {
  display: flex;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--u2-fill);
  border: 1px solid var(--u2-stroke);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.layer-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  background: rgba(var(--u2-primary-rgb), 0.06);
  color: var(--u2-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.layer-icon svg {
  width: 20px;
  height: 20px;
}

.layer-body h4 {
  margin: 0 0 var(--space-1);
  font-size: 14px;
}

.layer-body p {
  margin: 0;
  font-size: 12px;
  color: var(--u2-muted);
  line-height: 1.6;
}

.security-shield {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.shield-ring {
  position: absolute;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  border: 1px solid rgba(var(--u2-primary-rgb), 0.10);
}

.shield-ring::before {
  content: '';
  position: absolute;
  inset: 40px;
  border-radius: 50%;
  border: 1px solid rgba(var(--u2-primary-rgb), 0.14);
}

.shield-core {
  width: 160px;
}

/* Settlement */
.settlement-section {
  background: var(--u2-secondary);
  border-top: 1px solid var(--u2-stroke);
  border-bottom: 1px solid var(--u2-stroke);
}

.settle-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-5);
}

.settle-card {
  padding: var(--space-6);
}

.settle-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-3);
}

.settle-mode {
  font-size: 28px;
  font-weight: 700;
  color: var(--u2-text);
}

.settle-tag {
  font-size: 11px;
  font-weight: 700;
  color: var(--u2-primary);
  background: rgba(var(--u2-primary-rgb), 0.06);
  border: 1px solid rgba(var(--u2-primary-rgb), 0.22);
  padding: 2px var(--space-3);
  border-radius: var(--radius-full);
}

.settle-desc {
  color: var(--u2-muted);
  font-size: 13px;
  line-height: 1.7;
  margin: 0 0 var(--space-4);
  min-height: 44px;
}

.settle-bar {
  height: 6px;
  border-radius: var(--radius-full);
  background: var(--u2-hover);
  overflow: hidden;
  margin-bottom: var(--space-2);
}

.settle-bar span {
  display: block;
  height: 100%;
  border-radius: var(--radius-full);
  background: var(--u2-primary);
}

.settle-note {
  font-size: 11px;
  color: var(--u2-muted);
}

/* Developer */
.dev-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-12);
  align-items: center;
}

.dev-steps {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-bottom: var(--space-8);
}

.dev-step {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.dev-num {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(8, 145, 178, 0.08);
  border: 1px solid rgba(8, 145, 178, 0.25);
  color: var(--u2-info);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}

.dev-step h4 {
  margin: 0;
  font-size: 14px;
}

.dev-step p {
  margin: 0;
  font-size: 12px;
  color: var(--u2-muted);
}

.code-card {
  background: #0D1017;
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

.code-bar {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.code-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
}

.code-dot:first-child { background: var(--u2-primary); }

.code-badge {
  margin-left: auto;
  font-size: 11px;
  color: #8BE9C3;
  background: rgba(14, 159, 87, 0.14);
  border: 1px solid rgba(14, 159, 87, 0.35);
  padding: 2px var(--space-3);
  border-radius: var(--radius-full);
}

.code-body {
  margin: 0;
  padding: var(--space-5) var(--space-6);
  font-size: 13px;
  line-height: 1.8;
  color: #D7DEE8;
  overflow-x: auto;
}

.code-line:first-child {
  color: #7DD3FC;
  font-weight: 700;
}

/* Roles */
.roles-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--space-4);
}

.role-card {
  padding: var(--space-6);
  cursor: pointer;
}

.role-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  background: rgba(8, 145, 178, 0.08);
  color: var(--u2-info);
  margin-bottom: var(--space-4);
}

.role-icon svg {
  width: 24px;
  height: 24px;
}

.role-name {
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 var(--space-2);
}

.role-desc {
  color: var(--u2-muted);
  font-size: 13px;
  line-height: 1.7;
  margin: 0 0 var(--space-4);
}

.role-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: 13px;
  color: var(--u2-primary);
  font-weight: 700;
}

/* Onboarding */
.onboarding-section {
  background: linear-gradient(180deg, var(--u2-bg) 0%, var(--u2-secondary) 100%);
}

.section-header.center {
  text-align: center;
  max-width: 640px;
  margin: 0 auto var(--space-12);
}

.section-header.center .section-subtitle {
  margin-left: auto;
  margin-right: auto;
}

.onboarding-timeline {
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: var(--space-6);
  margin-bottom: var(--space-12);
}

.timeline-line {
  position: absolute;
  top: 24px;
  left: 10%;
  right: 10%;
  height: 2px;
  background: var(--u2-stroke);
}

.timeline-step {
  position: relative;
  text-align: center;
  flex: 1;
  z-index: 1;
}

.timeline-dot {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin: 0 auto var(--space-4);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--u2-fill);
  border: 2px solid var(--u2-primary);
  color: var(--u2-primary);
  font-weight: 700;
  font-size: 18px;
  box-shadow: var(--shadow-sm);
}

.timeline-step h3 {
  font-size: 18px;
  margin: 0 0 var(--space-2);
}

.timeline-step p {
  color: var(--u2-muted);
  font-size: 14px;
  margin: 0;
}

.onboarding-cta {
  text-align: center;
}

.btn-lg {
  padding: var(--space-4) var(--space-10);
  font-size: 16px;
}

/* Final CTA */
.final-cta-section {
  padding: var(--space-20) 0;
}

.final-cta-card {
  text-align: center;
  padding: var(--space-16) var(--space-8);
  border-radius: var(--radius-xl);
  background: linear-gradient(135deg, rgba(var(--u2-primary-rgb), 0.05) 0%, rgba(8, 145, 178, 0.05) 100%);
  border: 1px solid var(--u2-stroke);
}

.final-cta-card h2 {
  font-size: clamp(26px, 3.5vw, 36px);
  font-weight: 900;
  margin: 0 0 var(--space-3);
  letter-spacing: -0.02em;
}

.final-cta-card p {
  color: var(--u2-muted);
  margin: 0 0 var(--space-8);
}

.final-actions {
  display: flex;
  justify-content: center;
  gap: var(--space-4);
  flex-wrap: wrap;
}

/* Animations */
[data-animate] {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s var(--ease-out-expo), transform 0.7s var(--ease-out-expo);
}

[data-animate].visible {
  opacity: 1;
  transform: translateY(0);
}

/* Responsive */
@media (max-width: 1024px) {
  .hero-grid,
  .preview-grid,
  .security-grid {
    grid-template-columns: 1fr;
    gap: var(--space-10);
  }
  .hero-visual {
    order: -1;
  }
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .roles-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .pay-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .cap-grid,
  .settle-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .dev-grid {
    grid-template-columns: 1fr;
    gap: var(--space-10);
  }
}

@media (max-width: 768px) {
  .features-grid,
  .roles-grid,
  .kpi-grid,
  .pay-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .cap-grid,
  .settle-grid,
  .layer-grid {
    grid-template-columns: 1fr;
  }
  .journey-flow {
    flex-direction: column;
    gap: var(--space-4);
  }
  .journey-track {
    display: none;
  }
  .journey-node {
    flex-direction: row;
    align-items: center;
    gap: var(--space-4);
  }
  .journey-label {
    text-align: left;
    max-width: none;
  }
  .onboarding-timeline {
    flex-direction: column;
    gap: var(--space-8);
  }
  .timeline-line {
    display: none;
  }
  .hero-actions {
    flex-direction: column;
  }
  .hero-actions .btn {
    width: 100%;
  }
  .preview-stats {
    gap: var(--space-5);
    flex-wrap: wrap;
  }
}
</style>
